// Deterministic date normalization for Smart Asset Import.
//
// Real client files mix formats: ISO datetimes, Excel serial numbers, and dd.mm.yyyy
// strings. We normalize to ISO `YYYY-MM-DD` ONLY when the interpretation is unambiguous.
// When a value could be read two ways (e.g. 03/04/2025 — is it 3 Apr or 4 Mar?), we DO
// NOT guess: status = 'AMBIGUOUS' and the row is routed to review. Never silently pick
// a day/month order.

const pad2 = (n) => String(n).padStart(2, '0');

const toIso = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`;

// Excel serial -> Date. Excel's epoch is 1899-12-30 (accounting for the 1900 leap-year bug),
// matching the convention already used in excelLoader.js.
const excelSerialToParts = (serial) => {
  const excelEpoch = new Date(Date.UTC(1899, 11, 30));
  const d = new Date(excelEpoch.getTime() + serial * 24 * 60 * 60 * 1000);
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate() };
};

const validYmd = (y, m, d) =>
  Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d) &&
  m >= 1 && m <= 12 && d >= 1 && d <= 31 && y >= 1900 && y <= 2100;

/**
 * @returns {{ iso:string|null, status:'OK'|'AMBIGUOUS'|'EMPTY'|'INVALID', raw:*, note:string }}
 */
export const normalizeDate = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return { iso: null, status: 'EMPTY', raw: value, note: '' };
  }

  // Native Date object (xlsx may yield these with cellDates).
  if (value instanceof Date && !isNaN(value.getTime())) {
    return {
      iso: toIso(value.getFullYear(), value.getMonth() + 1, value.getDate()),
      status: 'OK', raw: value, note: 'from Date object',
    };
  }

  // Excel serial number.
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value > 0 && value < 80000) {
      const { y, m, d } = excelSerialToParts(value);
      if (validYmd(y, m, d)) return { iso: toIso(y, m, d), status: 'OK', raw: value, note: 'from Excel serial' };
    }
    return { iso: null, status: 'INVALID', raw: value, note: 'numeric but not a plausible date serial' };
  }

  const s = String(value).trim();

  // ISO-like: YYYY-MM-DD (optionally with time). Unambiguous.
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) {
    const y = +m[1], mo = +m[2], d = +m[3];
    if (validYmd(y, mo, d)) return { iso: toIso(y, mo, d), status: 'OK', raw: value, note: 'from ISO string' };
    return { iso: null, status: 'INVALID', raw: value, note: 'malformed ISO date' };
  }

  // dd.mm.yyyy / dd/mm/yyyy / dd-mm-yyyy (and 2-digit year). Day-first is the convention
  // for this client base (Indian/EU style), but we only commit when it is UNAMBIGUOUS.
  m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/);
  if (m) {
    let a = +m[1]; // candidate day
    let b = +m[2]; // candidate month
    let y = +m[3];
    if (y < 100) y += y < 50 ? 2000 : 1900; // 2-digit year heuristic (documented, not data-bearing)

    const dayFirstValid = validYmd(y, b, a); // a=day, b=month
    const monthFirstValid = validYmd(y, a, b); // a=month, b=day

    if (a > 12 && b <= 12) {
      // Only day-first reading is possible.
      return { iso: toIso(y, b, a), status: 'OK', raw: value, note: 'day-first (unambiguous)' };
    }
    if (b > 12 && a <= 12) {
      // Only month-first reading is possible.
      return { iso: toIso(y, a, b), status: 'OK', raw: value, note: 'month-first (unambiguous)' };
    }
    if (a <= 12 && b <= 12 && a !== b && dayFirstValid && monthFirstValid) {
      // Genuinely ambiguous (e.g. 03/04/2025). Do not guess.
      return {
        iso: null, status: 'AMBIGUOUS', raw: value,
        note: `ambiguous day/month order: could be ${toIso(y, b, a)} or ${toIso(y, a, b)}`,
      };
    }
    if (a === b && dayFirstValid) {
      // e.g. 05/05/2025 — order doesn't matter.
      return { iso: toIso(y, b, a), status: 'OK', raw: value, note: 'day==month' };
    }
    if (dayFirstValid) return { iso: toIso(y, b, a), status: 'OK', raw: value, note: 'day-first' };
    return { iso: null, status: 'INVALID', raw: value, note: 'unparseable numeric date' };
  }

  return { iso: null, status: 'INVALID', raw: value, note: 'unrecognized date format' };
};
